import '@/src/assets/styles/global.css';
import '@/src/assets/styles/helpers.css';
import '@/src/assets/styles/quill.css';

import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import deLocale from 'date-fns/locale/de';
import enLocale from 'date-fns/locale/en-US';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {appWithTranslation} from 'next-i18next';
import PropTypes from 'prop-types';
import {createContext, useEffect, useState} from 'react';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import {DARK_THEME} from '@/src/app/constants/theme';
import {wrapper} from '@/src/app/store';
import AppBootstrap from '@/src/appBootsrap/containers/AppBootstrap';
import {fetchThemeMode} from '@/src/appBootsrap/utils/fetchThemeMode';
import {HeaderHeightContext} from '@/src/header/context/HeaderHeightContext';
import Overlay from '@/src/overlay/containers/Overlay';
import PopupDisplayer from '@/src/popup/containers/PopupDisplayer';
import Routes from '@/src/routes/containers/Routes';

const LOCALE_MAP = {
  en: enLocale,
  de: deLocale,
};

const STAGING_ENV_VALUE = '1';

export const ThemeContext = createContext({
  isDarkMode: false,
});

const WrappedApp = ({Component, pageProps}) => {
  const {locale} = useRouter();
  const [themeMode, setthemeMode] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);

  const isStoryPageTheme = pageProps?.isDarkMode;

  const fetchDarkMode = async () => {
    const {status, responseBody} = await fetchThemeMode();

    if (status === RESPONSE_STATUSES.SUCCESS) {
      const {dark_mode} = responseBody;
      setthemeMode(dark_mode);
      localStorage.setItem('theme', dark_mode);
    } else {
      setthemeMode(false);
      localStorage.setItem('theme', 'false');
    }
  };

  useEffect(() => {
    const storedTheme = localStorage.getItem('theme');
    setthemeMode(storedTheme === 'true');
    fetchDarkMode();
  }, []);

  // // TODO remove if not needed, after test is done
  // useEffect(() => {
  //   TagManager.initialize({gtmId: `GTM-${process.env.NEXT_PUBLIC_GTM_KEY}`});
  // }, []);

  const isDarkMode = isStoryPageTheme ?? themeMode;
  return (
    <>
      <Head>
        <title>MedYouCate</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/favicon/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon/favicon-16x16.png"
        />
        <link rel="manifest" href="/favicon/site.webmanifest" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content={isDarkMode ? '#18385E' : '#FFFFFF'} />

        {process.env.NEXT_PUBLIC_STAGING === STAGING_ENV_VALUE && (
          <meta name="robots" content="noindex" />
        )}
      </Head>

      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        locale={LOCALE_MAP[locale]}
      >
        <Routes>
          <HeaderHeightContext.Provider value={{headerHeight, setHeaderHeight}}>
            <ThemeContext.Provider
              value={{
                isDarkMode: isStoryPageTheme ?? themeMode,
              }}
            >
              <div className={isDarkMode ? DARK_THEME : ''}>
                <AppBootstrap>
                  <>
                    <PopupDisplayer>
                      <>
                        <Overlay />
                        <Component {...pageProps} />
                      </>
                    </PopupDisplayer>
                  </>
                </AppBootstrap>
              </div>
            </ThemeContext.Provider>
          </HeaderHeightContext.Provider>
        </Routes>
      </LocalizationProvider>
    </>
  );
};

WrappedApp.propTypes = {
  Component: PropTypes.any,
  pageProps: PropTypes.object,
};

export default wrapper.withRedux(appWithTranslation(WrappedApp));
